import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import { MouseEventHandler } from 'react';

import { Box } from './Box';

interface SelectOptionProps extends MenuItemProps {
  label: string | React.ReactNode;
  value: string | number | undefined;
  clearable?: boolean;
}

interface TextFieldProps {
  inputRef?: MuiTextFieldProps['inputRef'];
  value?: MuiTextFieldProps['value'];
  onBlur?: MuiTextFieldProps['onBlur'];
  onChange?: MuiTextFieldProps['onChange'];
  onFocus?: MuiTextFieldProps['onFocus'];
  onKeyDown?: MuiTextFieldProps['onKeyDown'];
  onClick?: MouseEventHandler<HTMLElement>;
  type?: MuiTextFieldProps['type'];
  minRows?: MuiTextFieldProps['minRows'];
  maxRows?: MuiTextFieldProps['maxRows'];
  disabled?: boolean | 'click-only';
  autoComplete?: MuiTextFieldProps['autoComplete'];
  error?: MuiTextFieldProps['error'];
  helperText?: MuiTextFieldProps['helperText'];
  id?: MuiTextFieldProps['id'];
  multiline?: MuiTextFieldProps['multiline'];
  placeholder?: MuiTextFieldProps['placeholder'];
  label?: MuiTextFieldProps['label'];
  size?: MuiTextFieldProps['size'];
  rows?: MuiTextFieldProps['rows'];
  sx?: MuiTextFieldProps['sx'];
  variant?: 'outlined' | 'standard' | 'soft';
  fullWidth?: boolean;
  pill?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  InputProps?: MuiTextFieldProps['InputProps'];
  options?: SelectOptionProps[];
  customOptions?: React.ReactNode;
  slotProps?: MuiTextFieldProps['slotProps'];
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  name?: MuiTextFieldProps['name'];
}

/**
 * This component is a thin wrapper around MuiTextField.
 * 1. Removes the default floating label behavior and adds a static label (Can be re-enabled with floatingLabel prop)
 * 2. Allows setting startAdornment and endAdornment directly without InputProps
 * 3. Disallows filled variant
 */
export const TextField: React.FC<TextFieldProps> = ({ ...props }) => {
  const {
    inputRef,
    label,
    startAdornment,
    endAdornment,
    options,
    customOptions,
    size,
    sx,
    multiline,
    disabled,
    onClick,
    onFocus,
    pill,
    fullWidth = true,
    variant = 'outlined',
    resize = 'vertical',
    ...otherProps
  } = props;

  const isSelect = !!options || !!customOptions;

  // Handle focus behavior for "0" values - select all text to allow easy replacement
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const target = event.target;
    const currentValue = target.value;

    // If the value is exactly "0", select all text so user can immediately type a new number
    if (otherProps.type === 'number' && currentValue === '0') {
      target.select();
    }

    // Call the original onFocus handler if provided
    if (onFocus) {
      onFocus(event);
    }
  };

  const inputStyles = {
    [`&.${inputBaseClasses.multiline} .${inputBaseClasses.input}`]: {
      resize: resize,
    },
    [`& .${inputBaseClasses.input}.${inputBaseClasses.disabled}`]: {
      WebkitTextFillColor: (theme) =>
        disabled === 'click-only'
          ? theme.vars.palette.text.primary
          : theme.vars.palette.text.disabled,
    },
    ...(pill && {
      borderRadius: '50svh',
    }),
    ...(variant === 'soft' && {
      backgroundColor: (theme) =>
        theme.tokens.palette.field.soft.backgroundEnabled.light,
      border: 'none',
    }),
  };

  return (
    <FormLabel
      sx={[
        { display: 'block', position: 'relative' },
        fullWidth && { width: '100%' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <MuiTextField
        {...otherProps}
        label={label}
        fullWidth={fullWidth}
        inputRef={inputRef}
        multiline={multiline}
        disabled={!!disabled}
        size={size}
        select={isSelect}
        onFocus={handleFocus}
        variant={props.variant === 'soft' ? 'outlined' : props.variant}
        onWheel={(e) =>
          otherProps.type === 'number' &&
          e.target instanceof HTMLElement &&
          e.target.blur()
        }
        slotProps={{
          input: {
            startAdornment,
            endAdornment,
            sx: inputStyles,
            ...props.InputProps,
          },
          ...props.slotProps,
        }}
      >
        {isSelect &&
          (customOptions
            ? customOptions
            : options?.map((option) => (
                <MenuItem
                  {...option}
                  key={option.value}
                  value={option.value}
                  sx={{ my: 0.25, mx: 1 }}
                >
                  {option.label}
                </MenuItem>
              )))}
      </MuiTextField>

      {disabled === 'click-only' && (
        <Box
          onClick={onClick}
          tabIndex={0}
          role="button"
          component="button"
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: '0px',
            top: '0px',
            bgcolor: 'transparent',
            borderColor: 'transparent',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'transparent',
              borderColor: 'transparent',
            },
          }}
        ></Box>
      )}
    </FormLabel>
  );
};

export { InputAdornment };
export type { TextFieldProps };
